import type {
  BootstrapPayload,
  CartFormPayload,
  CartPayload,
  CatalogCategory,
  CatalogImportLaunchPayload,
  CatalogImportStatusPayload,
  CatalogProductsPayload,
  CurrentSquareDetailPayload,
  DirectoryDetailPayload,
  DirectoryEntryPayload,
  DirectoryListPayload,
  DashboardPayload,
  FlowPayload,
  FlowSubmitResponse,
  MessengerLinkPayload,
  MessengerLinkStatusPayload,
  PartnerCabinetCreatePayload,
  PartnerCabinetDetailPayload,
  PartnerCabinetFormPayload,
  PartnerCabinetListPayload,
  PartnerCabinetOptionsPayload,
  PartnerCabinetPreviewPayload,
  PartnerCabinetTelegramLinkPayload,
  PartnerCabinetTelegramLinkStatusPayload,
  ProfilePayload,
  ReportBuilderColumn,
  ReportBuilderPayload,
  ReportDetailPayload,
  ReportListPayload,
  SearchPayload,
  SessionPayload,
  SquareSortDirection,
  SquareListPayload,
  SquareDetailPayload,
  ApplicationDetailPayload,
} from '@/types/api'
import { getCookie } from '@/lib/utils'

let csrfTokenCache = ''

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  body?: BodyInit | null
  headers?: HeadersInit
}

type SquareDetailParams = {
  page?: number
  query?: string
  sortKey?: string | null
  sortDirection?: SquareSortDirection | null
}

export const API_BASE_URL = window.__CRM_SPA__?.apiBaseUrl ?? '/api'

declare global {
  interface Window {
    __CRM_SPA__?: {
      apiBaseUrl: string
      entryPath: string
    }
  }
}

class ApiError extends Error {
  status: number
  data: unknown

  constructor(message: string, status: number, data: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.data = data
  }
}

function syncCsrfToken(csrfToken?: string | null) {
  if (!csrfToken) {
    return
  }

  csrfTokenCache = csrfToken
}

async function ensureCsrfToken() {
  const cachedToken = csrfTokenCache || getCookie('csrftoken')
  if (cachedToken) {
    return cachedToken
  }

  try {
    const session = await request<SessionPayload>('/auth/session', {}, false)
    return session.csrfToken || csrfTokenCache || getCookie('csrftoken')
  } catch {
    return ''
  }
}

async function request<T>(
  path: string,
  options: RequestOptions = {},
  retryOnForbidden = true,
): Promise<T> {
  const headers = new Headers(options.headers)
  headers.set('Accept', 'application/json')

  if (options.method && options.method !== 'GET') {
    const csrfToken = await ensureCsrfToken()
    if (csrfToken) {
      headers.set('X-CSRFToken', csrfToken)
    }
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: options.method ?? 'GET',
    body: options.body,
    headers,
    credentials: 'include',
  })

  const contentType = response.headers.get('content-type') ?? ''
  const data = contentType.includes('application/json')
    ? await response.json()
    : await response.text()

  if (typeof data === 'object' && data && 'csrfToken' in data) {
    syncCsrfToken(typeof data.csrfToken === 'string' ? data.csrfToken : null)
  }

  if (!response.ok) {
    if (
      retryOnForbidden &&
      response.status === 403 &&
      options.method &&
      options.method !== 'GET' &&
      path !== '/auth/session'
    ) {
      try {
        await request<SessionPayload>('/auth/session', {}, false)
        return await request<T>(path, options, false)
      } catch {
        // Если повтор с обновленным CSRF не помог, отдадим исходную ошибку ниже.
      }
    }

    throw new ApiError(
      typeof data === 'object' && data
        ? 'message' in data
          ? String(data.message)
          : 'error' in data
            ? String(data.error)
            : 'detail' in data
              ? String(data.detail)
              : 'Не удалось выполнить запрос.'
        : 'Не удалось выполнить запрос.',
      response.status,
      data,
    )
  }

  return data as T
}

export { ApiError }

export const api = {
  session: () => request<SessionPayload>('/auth/session'),
  login: (username: string, password: string) =>
    request<SessionPayload>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
      headers: {
        'Content-Type': 'application/json',
      },
    }),
  logout: () =>
    request<{ success: boolean }>('/auth/logout', {
      method: 'POST',
    }),
  profile: () => request<ProfilePayload>('/profile'),
  telegramLinkStatus: () => request<MessengerLinkStatusPayload>('/profile/messenger-links/telegram'),
  generateTelegramLink: () =>
    request<MessengerLinkPayload>('/profile/messenger-links/telegram', {
      method: 'POST',
    }),
  maxLinkStatus: () => request<MessengerLinkStatusPayload>('/profile/messenger-links/max'),
  generateMaxLink: () =>
    request<MessengerLinkPayload>('/profile/messenger-links/max', {
      method: 'POST',
    }),
  updateProfile: (body: FormData) =>
    request<ProfilePayload>('/profile', {
      method: 'PUT',
      body,
    }),
  bootstrap: () => request<BootstrapPayload>('/bootstrap'),
  partnerCabinets: () => request<PartnerCabinetListPayload>('/admin/partner-cabinets'),
  partnerCabinetOptions: () =>
    request<PartnerCabinetOptionsPayload>('/admin/partner-cabinets/options'),
  previewPartnerCabinet: (payload: PartnerCabinetFormPayload) =>
    request<PartnerCabinetPreviewPayload>('/admin/partner-cabinets/preview', {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: {
        'Content-Type': 'application/json',
      },
    }),
  createPartnerCabinet: (payload: PartnerCabinetFormPayload) =>
    request<PartnerCabinetCreatePayload>('/admin/partner-cabinets/create', {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: {
        'Content-Type': 'application/json',
      },
    }),
  partnerCabinetDetail: (cityId: string) =>
    request<PartnerCabinetDetailPayload>(`/admin/partner-cabinets/${cityId}`),
  generatePartnerCabinetTelegramLink: (cityId: string) =>
    request<PartnerCabinetTelegramLinkPayload>(`/admin/partner-cabinets/${cityId}/telegram-link`, {
      method: 'POST',
    }),
  partnerCabinetTelegramLinkStatus: (cityId: string) =>
    request<PartnerCabinetTelegramLinkStatusPayload>(`/admin/partner-cabinets/${cityId}/telegram-link`),
  dashboard: () => request<DashboardPayload>('/dashboard'),
  search: (query: string) =>
    request<SearchPayload>(`/search?query=${encodeURIComponent(query)}`),
  squares: () => request<SquareListPayload>('/squares'),
  squareDetail: (squareId: string, params: SquareDetailParams = {}) => {
    const search = new URLSearchParams()
    if (params.page && params.page > 1) {
      search.set('page', String(params.page))
    }
    if (params.query) {
      search.set('query', params.query)
    }
    if (params.sortKey) {
      search.set('sort_key', params.sortKey)
    }
    if (params.sortDirection) {
      search.set('sort_direction', params.sortDirection)
    }

    return request<SquareDetailPayload>(
      `/squares/${squareId}${search.size ? `?${search.toString()}` : ''}`,
    )
  },
  currentSquareDetail: (squareId: string, mode: 'days' | 'month', monthOffset = 0) =>
    request<CurrentSquareDetailPayload>(
      `/current-squares/${squareId}?mode=${mode}&month_offset=${monthOffset}`,
    ),
  applicationDetail: (applicationId: string) =>
    request<ApplicationDetailPayload>(`/applications/${applicationId}`),
  postComment: (applicationId: string, comment: string) =>
    request(`/applications/${applicationId}/comments`, {
      method: 'POST',
      body: JSON.stringify({ comment }),
      headers: {
        'Content-Type': 'application/json',
      },
    }),
  deleteApplication: (applicationId: string) =>
    request(`/applications/${applicationId}/delete`, {
      method: 'POST',
    }),
  restoreApplication: (applicationId: string) =>
    request(`/applications/${applicationId}/restore`, {
      method: 'POST',
    }),
  applicationCart: (applicationId: string) =>
    request<CartPayload>(`/applications/${applicationId}/cart`),
  updateApplicationCart: (applicationId: string, payload: CartFormPayload) =>
    request<CartPayload>(`/applications/${applicationId}/cart`, {
      method: 'PUT',
      body: JSON.stringify(payload),
      headers: {
        'Content-Type': 'application/json',
      },
    }),
  createFlow: (schemaId: string, stageId: string) =>
    request<FlowPayload>(`/application-flows/create/${schemaId}/${stageId}`),
  submitCreateFlow: (schemaId: string, stageId: string, body: FormData) =>
    request<FlowSubmitResponse>(`/application-flows/create/${schemaId}/${stageId}`, {
      method: 'POST',
      body,
    }),
  editFlow: (applicationId: string) =>
    request<FlowPayload>(`/application-flows/${applicationId}/edit`),
  submitEditFlow: (applicationId: string, body: FormData) =>
    request<FlowSubmitResponse>(`/application-flows/${applicationId}/edit`, {
      method: 'PUT',
      body,
    }),
  transitionFlow: (applicationId: string, transitionId: string) =>
    request<FlowPayload>(`/application-flows/${applicationId}/transition/${transitionId}`),
  submitTransitionFlow: (applicationId: string, transitionId: string, body: FormData) =>
    request<FlowSubmitResponse>(`/application-flows/${applicationId}/transition/${transitionId}`, {
      method: 'POST',
      body,
    }),
  catalogCategories: () =>
    request<CatalogCategory[]>('/catalog/categories/'),
  catalogProducts: (query: URLSearchParams) =>
    request<CatalogProductsPayload>(`/catalog/products/?${query.toString()}`),
  startCatalogImport: (body: FormData) =>
    request<CatalogImportLaunchPayload>('/catalog/import-yml-background/', {
      method: 'POST',
      body,
    }),
  catalogImportStatus: (jobId: string) =>
    request<CatalogImportStatusPayload>(`/catalog/import-job-status/${jobId}/`),
  directories: () => request<DirectoryListPayload>('/directories'),
  directoryDetail: (directoryId: string) =>
    request<DirectoryDetailPayload>(`/directories/${directoryId}`),
  directoryEntry: (entryId: string) =>
    request<DirectoryEntryPayload>(`/directory-entries/${entryId}`),
  createDirectoryEntry: (directoryId: string, body: FormData) =>
    request<{ entryId: number; directoryId: number; redirectPath: string }>(
      `/directories/${directoryId}/entries`,
      {
        method: 'POST',
        body,
      },
    ),
  updateDirectoryEntry: (entryId: string, body: FormData) =>
    request<{ entryId: number; directoryId: number; redirectPath: string }>(
      `/directory-entries/${entryId}`,
      {
        method: 'PUT',
        body,
      },
    ),
  deleteDirectoryEntry: (entryId: string) =>
    request<{ success: boolean; directoryId: number; redirectPath: string }>(`/directory-entries/${entryId}`, {
      method: 'DELETE',
    }),
  reports: (categoryId?: string) =>
    request<ReportListPayload>(
      `/reports${categoryId ? `?category_id=${encodeURIComponent(categoryId)}` : ''}`,
    ),
  reportDetail: (reportId: string, params?: { startDate?: string; endDate?: string }) => {
    const search = new URLSearchParams()
    if (params?.startDate) {
      search.set('start_date', params.startDate)
    }
    if (params?.endDate) {
      search.set('end_date', params.endDate)
    }
    return request<ReportDetailPayload>(
      `/reports/${reportId}${search.size ? `?${search.toString()}` : ''}`,
    )
  },
  createReport: (payload: { name: string; description: string }) =>
    request<{ reportId: number; redirectPath: string }>('/reports/create', {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: {
        'Content-Type': 'application/json',
      },
    }),
  reportBuilder: (reportId: string) =>
    request<ReportBuilderPayload>(`/reports/${reportId}/builder`),
  updateReport: (reportId: string, payload: { name: string; description: string }) =>
    request<{ reportId: number; redirectPath: string }>(`/reports/${reportId}/update`, {
      method: 'PUT',
      body: JSON.stringify(payload),
      headers: {
        'Content-Type': 'application/json',
      },
    }),
  cloneReport: (reportId: string) =>
    request<{ reportId: number; redirectPath: string }>(`/reports/${reportId}/clone`, {
      method: 'POST',
    }),
  createReportColumn: (reportId: string, payload: Omit<ReportBuilderColumn, 'id'>) =>
    request<{ columnId: number }>(`/reports/${reportId}/columns`, {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: {
        'Content-Type': 'application/json',
      },
    }),
  updateReportColumn: (reportId: string, columnId: string, payload: Omit<ReportBuilderColumn, 'id'>) =>
    request<{ columnId: number }>(`/reports/${reportId}/columns/${columnId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
      headers: {
        'Content-Type': 'application/json',
      },
    }),
  deleteReportColumn: (reportId: string, columnId: string) =>
    request<{ success: boolean }>(`/reports/${reportId}/columns/${columnId}`, {
      method: 'DELETE',
    }),
}
